import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
  MatTabChangeEvent,
  MatTabGroup,
} from '@angular/material/tabs';
import { firstValueFrom } from 'rxjs';
import {
  ImageUpload,
  ManagementService,
  ReportsService,
} from 'src/app/api/generated';
import { DataChangedService } from 'src/app/data-changed.service';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import {
  createImageObjectUrl,
  revokeImageObjectUrl,
  toApiImageUrl,
} from 'src/app/tools/image-url';
import { ImageViewerConfig } from '../../image-viewer.tools';

import * as uuid from 'uuid';
import { validURL } from 'src/app/tools/validators';
import { environment } from 'src/environments/environment';

enum SELECTED_TAB {
  Webcam = 0,
  ImageUpload,
}

@Component({
    selector: 'app-image-viewer-config',
    templateUrl: './image-viewer-config.component.html',
    styleUrls: ['./image-viewer-config.component.scss'],
    standalone: false
})
export class ImageViewerConfigComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public imagePreviewURL: string | null = null;
  public imageViewerForm: UntypedFormGroup;
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  @ViewChild('image', { static: false }) image: ElementRef;

  public editMode = false;
  public submitting = false;
  private destroyed = false;
  private imageFetchId = 0;
  private imagePreviewObjectUrl: string | null = null;
  private tabSelectionTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImageViewerConfig,
    private dialogRef: MatDialogRef<ImageViewerConfig>,
    private formBuilder: UntypedFormBuilder,
    private reportModuleService: ReportModulesService,
    private dataChangedService: DataChangedService,
    private reportService: ReportsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.editMode = this.data?.guid !== undefined;
    this.imageViewerForm = this.formBuilder.group({
      guid: [this.data?.guid ? this.data.guid : uuid.v4()],
      type: ['image-viewer'],
      webcamURL: [
        this.data?.refresh ? this.data?.resource_url : '',
        [Validators.required, validURL()],
      ],
      webcamRefreshRate: [
        this.data?.refresh_timer ? this.data?.refresh_timer / 60 : '',
        [Validators.required],
      ],
      imageSource: ['', [Validators.required]],
      headline: [this.data?.headline ? this.data?.headline : ''],
      imageCaption: [this.data?.description ? this.data?.description : ''],
      selectedTab: [0, Validators.required],
    });
  }

  async ngAfterViewInit() {
    if (this.editMode) {
      if (!this.data?.refresh) {
        await this.fetchImageAndDisplay();
      } else {
        this.tabChanged({
          index: SELECTED_TAB.Webcam,
          tab: undefined,
        });
      }
    } else {
      this.tabChanged({
        index: SELECTED_TAB.Webcam,
        tab: undefined,
      });
    }
  }

  async fetchImageAndDisplay(): Promise<void> {
    const fetchId = ++this.imageFetchId;
    const imageURL = toApiImageUrl(
      environment.apiEndpoint,
      this.data.resource_url
    );
    if (imageURL === null || !this.imageFetchIsActive(fetchId)) {
      return;
    }

    const response = await fetch(imageURL);
    if (!response.ok || !this.imageFetchIsActive(fetchId)) {
      return;
    }
    const blob = await response.blob();
    if (!this.imageFetchIsActive(fetchId)) {
      return;
    }
    const objectUrl = createImageObjectUrl(blob);
    if (objectUrl === null) {
      return;
    }
    if (!this.imageFetchIsActive(fetchId)) {
      revokeImageObjectUrl(objectUrl);
      return;
    }

    this.setImagePreview(objectUrl);
    this.imageViewerForm.get('imageSource').patchValue(blob);

    this.cdr.detectChanges();
    // Not sure why this is not working
    this.tabSelectionTimeout = setTimeout(() => {
      this.tabSelectionTimeout = null;
      if (!this.imageFetchIsActive(fetchId)) {
        return;
      }
      this.tabGroup.selectedIndex = SELECTED_TAB.ImageUpload;
      this.tabChanged({
        index: SELECTED_TAB.ImageUpload,
        tab: undefined,
      });
    }, 200);
  }

  public onFileChange(event) {
    if (event.target.files?.length > 0) {
      const [file] = event.target.files;
      const objectUrl = createImageObjectUrl(file);
      if (objectUrl === null) {
        return;
      }

      this.setImagePreview(objectUrl);
      this.imageViewerForm.get('imageSource').patchValue(file);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.imageFetchId++;
    if (this.tabSelectionTimeout !== null) {
      clearTimeout(this.tabSelectionTimeout);
      this.tabSelectionTimeout = null;
    }
    revokeImageObjectUrl(this.imagePreviewObjectUrl);
    this.imagePreviewObjectUrl = null;
  }

  public async updateOrCreateModule() {
    if (this.imageViewerForm.valid) {
      this.submitting = true;
      let resourceURL = '';
      if (this.imageViewerForm.value.selectedTab === SELECTED_TAB.ImageUpload) {
        try {
          const uploadedImage = await firstValueFrom(
            this.reportService.uploadsReportImage(
              this.imageViewerForm.value.imageSource
            )
          );
          resourceURL = uploadedImage.image_path;
        } catch (error) {
          this.submitting = false;
          console.error(error);
          return;
        }
      } else {
        resourceURL = this.imageViewerForm.value.webcamURL;
      }

      this.reportModuleService
        .addOrEditModuleToReport(
          <ImageViewerConfig>{
            guid: this.imageViewerForm.value.guid,
            type: this.imageViewerForm.value.type,
            description: this.imageViewerForm.value.imageCaption,
            headline: this.imageViewerForm.value.headline,
            refresh:
              this.imageViewerForm.value.selectedTab === SELECTED_TAB.Webcam,
            refresh_timer:
              Number(this.imageViewerForm.value.webcamRefreshRate) * 60,
            resource_url: resourceURL,
          },
          this.editMode
        )
        .subscribe(
          (success) => {
            this.submitting = false;
            this.dialogRef.close();
            this.dataChangedService.configUpdated(
              this.reportModuleService.currentReport.id,
              this.imageViewerForm.value.guid
            );
          },
          (error) => {
            this.submitting = false;
            console.error(error);
          }
        );
    }
  }

  public tabChanged(event: MatTabChangeEvent) {
    this.imageViewerForm.get('selectedTab').setValue(event.index);

    if (event.index === SELECTED_TAB.Webcam) {
      this.imageViewerForm
        .get('webcamURL')
        .setValidators([Validators.required, validURL()]);
      this.imageViewerForm
        .get('webcamRefreshRate')
        .setValidators([Validators.required]);

      this.imageViewerForm.get('imageSource').setValidators([]);
    } else {
      this.imageViewerForm.get('webcamURL').setValidators([]);
      this.imageViewerForm.get('webcamRefreshRate').setValidators([]);

      this.imageViewerForm
        .get('imageSource')
        .setValidators([Validators.required]);
    }

    this.imageViewerForm.get('webcamURL').updateValueAndValidity();
    this.imageViewerForm.get('webcamRefreshRate').updateValueAndValidity();
    this.imageViewerForm.get('imageSource').updateValueAndValidity();
  }

  private setImagePreview(value: string): void {
    revokeImageObjectUrl(this.imagePreviewObjectUrl);
    this.imagePreviewObjectUrl = value;
    this.imagePreviewURL = value;
  }

  private imageFetchIsActive(fetchId: number): boolean {
    return !this.destroyed && fetchId === this.imageFetchId;
  }
}
